import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useLang } from '../utils/LanguageContext';
import { t } from '../utils/translations';
import './LoginScreen.css';

function LoginScreen() {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  // Pre-fill the last email used, so an accidental logout only costs a password
  const [email, setEmail] = useState(() => localStorage.getItem('stratos_last_email') || '');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const { lang, toggleLang } = useLang();
  const tr = t[lang];

  async function handleForgot() {
    setError('');
    setInfo('');
    if (!email) {
      setError(tr.enterEmailFirst);
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (error) {
      const msg = (error.message || '').toLowerCase();
      setError(
        msg.includes('rate') || msg.includes('security') || error.status === 429
          ? tr.tooManyEmails
          : tr.resetError
      );
    } else {
      setInfo(tr.resetEmailSent);
    }
    setBusy(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setInfo('');

    if (mode === 'signup' && password.length < 8) {
      setError(tr.passwordShort);
      return;
    }

    setBusy(true);
    if (mode === 'signin') {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(tr.authError);
      else localStorage.setItem('stratos_last_email', email);
      // Success: AuthContext picks up the session and the app re-renders.
    } else {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName } },
      });
      if (error) {
        const msg = (error.message || '').toLowerCase();
        setError(
          msg.includes('already') ? tr.emailTaken :
          msg.includes('password') ? tr.passwordShort :
          tr.signUpError
        );
      } else if (data.user && data.user.identities?.length === 0) {
        // Supabase returns a fake success for existing confirmed emails
        setError(tr.emailTaken);
      } else if (data.user && !data.session) {
        // Email confirmation is enabled in Supabase
        setInfo(tr.checkEmail);
      }
    }
    setBusy(false);
  }

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={handleSubmit}>
        <button type="button" className="lang-btn login-lang" onClick={toggleLang}>
          {lang === 'fr' ? 'FR' : lang === 'en' ? 'EN' : 'ع'}
        </button>

        <img className="login-logo" src={process.env.PUBLIC_URL + '/logo192.png'} alt="StratosPOS" />
        <h1 className="login-title">StratosPOS</h1>
        <p className="login-sub">{mode === 'signin' ? tr.signIn : tr.signUp}</p>

        {mode === 'signup' && (
          <p className="login-join-hint">{tr.joinHint}</p>
        )}

        {mode === 'signup' && (
          <input
            type="text"
            placeholder={tr.fullName}
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            className="login-input login-input-text"
            autoComplete="name"
          />
        )}

        <input
          type="email"
          placeholder={tr.email}
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="login-input login-input-text"
          autoComplete="email"
          required
          autoFocus
        />

        <input
          type="password"
          placeholder={tr.password}
          value={password}
          onChange={e => setPassword(e.target.value)}
          className={`login-input login-input-text ${error ? 'shake' : ''}`}
          autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
          required
        />

        {error && <p className="login-error">❌ {error}</p>}
        {info && <p className="login-info">{info}</p>}

        <button type="submit" className="login-btn" disabled={busy}>
          {busy ? '...' : mode === 'signin' ? tr.signIn : tr.signUp}
        </button>

        {mode === 'signin' && (
          <button type="button" className="login-switch" onClick={handleForgot} disabled={busy}>
            {tr.forgotPassword}
          </button>
        )}

        <button
          type="button"
          className="login-switch"
          onClick={() => {
            setMode(mode === 'signin' ? 'signup' : 'signin');
            setError('');
            setInfo('');
          }}
        >
          {mode === 'signin' ? tr.noAccount : tr.haveAccount}
        </button>

        <a href="/legal" className="login-legal">{tr.privacyPolicy} · {tr.termsOfService}</a>
      </form>
    </div>
  );
}

export default LoginScreen;
